import React, { useState, useEffect } from 'react';
import Breadcrumb from '../../components/ui/Breadcrumb';
import MetricCard from './components/MetricCard';
import InventoryStatusTable from './components/InventoryStatusTable';
import ActivityFeed from './components/ActivityFeed';
import InventoryChart from './components/InventoryChart';
import QuickActions from './components/QuickActions';
import SearchBar from './components/SearchBar';
import api from '../../utils/api';
import { auth } from '../../utils/firebase';

const Dashboard = () => {
  const [metricsData, setMetricsData] = useState([
    { title: 'Total Inventory Value', value: null, format: 'currency', icon: 'DollarSign', color: 'primary' },
    { title: 'Low Stock Alerts', value: null, icon: 'AlertTriangle', color: 'warning' },
    { title: 'Units Sold (30 days)', value: null, icon: 'TrendingUp', color: 'success' },
    { title: 'Products Sold (30 days)', value: null, icon: 'Package', color: 'primary' }
  ]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [incompleteSalesHistory, setIncompleteSalesHistory] = useState(0);
  const [user, setUser] = useState(null);
  
  // Fetch user data
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);
  
  // Fetch dashboard metrics data
  useEffect(() => {
    let active = true;
    const fetchDashboardData = async () => {
      setLoading(true);
      setError('');
      try {
        const { data } = await api.get('/reports/analytics');
        if (!Array.isArray(data.products) || !Array.isArray(data.sales)) {
          throw new Error('Dashboard analytics response has an unexpected shape');
        }
        if (!active) return;

        const products = data.products;
        const sales = data.sales;
        const hasInventory = products.length > 0;
        const isNumber = value => value !== null && value !== undefined && value !== ''
          && Number.isFinite(Number(value));
        const completeValuation = products.every(product => isNumber(product.quantity) && isNumber(product.cost));
        const completeStockLevels = products.every(product => isNumber(product.quantity) && isNumber(product.reorderPoint));
        const totalValue = hasInventory && completeValuation
          ? products.reduce((sum, product) => sum + Number(product.quantity) * Number(product.cost), 0)
          : null;
        const lowStockCount = hasInventory && completeStockLevels
          ? products.filter(product => Number(product.quantity) <= Number(product.reorderPoint)).length
          : null;

        const now = Date.now();
        const thirtyDaysAgo = now - 30 * 24 * 60 * 60 * 1000;
        const recentSales = sales.filter(sale => {
          const soldAt = new Date(sale.createdAt).getTime();
          return Number.isFinite(soldAt) && soldAt >= thirtyDaysAgo && soldAt <= now;
        });
        const salesAreComplete = Number(data.incompleteSalesHistory) === 0;
        const unitsSold = salesAreComplete
          ? recentSales.reduce((sum, sale) => sum + Number(sale.quantity || 0), 0)
          : null;
        const productsSold = salesAreComplete && recentSales.every(sale => sale.productId)
          ? new Set(recentSales.map(sale => sale.productId).filter(Boolean)).size
          : null;

        setIncompleteSalesHistory(Number(data.incompleteSalesHistory) || 0);
        setMetricsData([
          { title: 'Total Inventory Value', value: totalValue, format: 'currency', icon: 'DollarSign', color: 'primary' },
          { title: 'Low Stock Alerts', value: lowStockCount, icon: 'AlertTriangle', color: 'warning' },
          { title: 'Units Sold (30 days)', value: unitsSold, icon: 'TrendingUp', color: 'success' },
          { title: 'Products Sold (30 days)', value: productsSold, icon: 'Package', color: 'primary' }
        ]);
      } catch (err) {
        console.error('Failed to fetch dashboard data:', err);
        if (active) {
          setMetricsData([
            { title: 'Total Inventory Value', value: null, format: 'currency', icon: 'DollarSign', color: 'primary' },
            { title: 'Low Stock Alerts', value: null, icon: 'AlertTriangle', color: 'warning' },
            { title: 'Units Sold (30 days)', value: null, icon: 'TrendingUp', color: 'success' },
            { title: 'Products Sold (30 days)', value: null, icon: 'Package', color: 'primary' }
          ]);
          setError('Dashboard data could not be loaded from the backend.');
        }
      } finally {
        if (active) setLoading(false);
      }
    };
    
    fetchDashboardData();
    return () => {
      active = false;
    };
  }, []);

  return (
    <div className="w-full px-3 sm:px-4 lg:px-6 py-4 sm:py-6 lg:py-8">
      <Breadcrumb />
      
      {/* Header Section */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-4 sm:mb-6 lg:mb-8">
        <div>
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-semibold text-foreground mb-1 sm:mb-2">
            Inventory Dashboard
          </h1>
          <p className="text-xs sm:text-sm lg:text-base text-muted-foreground">
            Welcome back, {user?.displayName || 'there'}! Here's what's happening with your inventory today.
          </p>
        </div>
        
        <div className="mt-3 lg:mt-0 flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3 lg:space-x-4">
          <SearchBar />
          {/* <div className="flex items-center space-x-2 text-xs sm:text-sm text-muted-foreground">
            <div className="w-2 h-2 bg-success rounded-full animate-pulse" />
            <span>Backend data</span>
          </div> */}
        </div>
      </div>

      {/* Metrics Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 lg:gap-4 xl:gap-6 mb-4 sm:mb-6 lg:mb-8">
        {metricsData?.map((metric, index) => (
          <MetricCard
            key={index}
            title={metric?.title}
            value={metric?.value === null ? 'N/A' : metric?.format === 'currency'
              ? `$${metric.value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
              : metric?.value?.toLocaleString()}
            change={metric?.change}
            changeType={metric?.changeType}
            icon={metric?.icon}
            color={metric?.color}
          />
        ))}
      </div>
      {error && <p role="alert" className="mb-4 text-sm text-destructive">{error}</p>}
      {incompleteSalesHistory > 0 && !error && (
        <p role="status" className="mb-4 text-sm text-warning">
          Sales totals are unavailable because some older checkout records do not contain reliable per-sale quantities.
        </p>
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6 xl:gap-8 mb-4 sm:mb-6 lg:mb-8">
        {/* Left Column - Inventory Status Table */}
        <div className="lg:col-span-2 order-2 lg:order-1">
          <InventoryStatusTable />
        </div>

        {/* Right Column - Activity Feed */}
        <div className="lg:col-span-1 order-1 lg:order-2">
          <ActivityFeed />
        </div>
      </div>

      {/* Charts and Quick Actions */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-3 sm:gap-4 lg:gap-6 xl:gap-8">
        {/* Analytics Chart */}
        <div className="xl:col-span-2">
          <InventoryChart />
        </div>

        {/* Quick Actions */}
        <div className="xl:col-span-1">
          <QuickActions />
        </div>
      </div>
    </div>
  );
};

export default Dashboard;